import React, { useMemo } from 'react';
import { merge } from 'lodash';
import { arrayOf, string, bool, shape, object } from 'prop-types';
import classnames from 'classnames';
import Scroller from '../Scroller';
import {
    getDisplayedNotableClients,
    alwaysRight,
    firstBottomLeftPosition
} from '../../../utils/helpers/notableClients';
import ClientList from '../ClientList';
import ClientLogo from '../ClientLogo';
import TopClientsTitle from '../TopClientsTitle';
import {
    reportNotableClientsHovered,
    trackUnknownInteraction,
    trackInteractionByOrigin,
    SOURCES
} from '../../../utils/reports';
import notableClientType from '../../../utils/types/notableClient';
import './style.scss';

const CLASS_NAME = 'notable-clients-bar';

const NotableClientsBar = ({
    notableClients,
    seller = {},
    wrap = false,
    titleProps: initialTitleProps = {},
    hideClientPopover = false,
    isSlimDesign = false,
    origin,
    detailsPopoverProps
}) => {
    const { name: sellerName, profilePhoto } = seller;
    const displayClients = useMemo(() => getDisplayedNotableClients(notableClients), [notableClients]);
    const titleClass = 'tbody-6 m-t-4';

    const titleProps = useMemo(() => {
        const conditionProps = isSlimDesign
            ? {
                sellerName,
                educate: false,
                isWrapped: wrap,
                newBadge: false,
                classNames: { wrapper: 'm-b-16', title: titleClass }
            }
            : {
                sellerName,
                educate: true,
                isWrapped: wrap,
                newBadge: false,
                classNames: { title: titleClass }
            };

        return merge(conditionProps, initialTitleProps);
    }, [isSlimDesign, wrap, sellerName, initialTitleProps]);

    const popoverPositionFunction = isSlimDesign ? alwaysRight : firstBottomLeftPosition;
    const clientListProps = {
        logoSize: isSlimDesign ? ClientLogo.SIZES.MEDIUM : ClientLogo.SIZES.SMALL,
        displayClients,
        sellerName,
        profilePhoto,
        popoverDelay: 300,
        hidePopover: hideClientPopover,
        popoverPositionFunction,
        clientHoverCallback: (client, position) => {
            reportNotableClientsHovered({
                client,
                position,
                origin,
                allClients: displayClients,
                component: SOURCES.ABOVE_THE_FOLD
            });
            trackInteractionByOrigin({
                seller,
                client,
                origin,
                allClients: displayClients
            }, trackUnknownInteraction);
        },
        detailsPopoverProps
    };

    return (
        <div className={classnames(CLASS_NAME, { 'wrap-elements': wrap })}>
            <TopClientsTitle {...titleProps}/>
            <Scroller className="scroll-hider" bringIntoView>
                <ClientList {...clientListProps}/>
            </Scroller>
        </div>
    );
};

NotableClientsBar.propTypes = {
    notableClients: arrayOf(notableClientType),
    seller: shape({
        name: string,
        profilePhoto: string
    }),
    titleProps: object,
    hideClientPopover: bool,
    wrap: bool,
    isSlimDesign: bool,
    origin: string,
    detailsPopoverProps: shape({
        title: string
    })
};

export default NotableClientsBar;
